/**
 * PremiumInsights - Carousel insight cards with share functionality
 * Pro-only feature showing travel pattern intelligence
 */
import { useState, useEffect, useRef } from "react";
import { 
  Sparkles, Compass, Calendar, Sun, Repeat, Activity, 
  MapPin, Heart, Share2, Lock, ChevronLeft, ChevronRight 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { shareViaWebAPI } from "@/hooks/useNativeShare";
import axios from "axios";
import { API } from "@/App";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

const iconMap = {
  compass: Compass,
  calendar: Calendar,
  sun: Sun,
  repeat: Repeat,
  activity: Activity,
  "map-pin": MapPin,
  heart: Heart,
};

const PremiumInsights = () => {
  const { hasUniversalAccess, hasPaidAccess } = useEntitlements();
  const hasAccess = hasUniversalAccess || hasPaidAccess;
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef(null);
  
  useEffect(() => {
    fetchInsights();
  }, []);
  
  const fetchInsights = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/premium/insights`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch insights:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleShare = async (insight) => {
    if (!insight.share_text) return;
    
    const shared = await shareViaWebAPI(insight.share_text, window.location.origin, insight.title);
    if (!shared) {
      try {
        await navigator.clipboard.writeText(insight.share_text);
        toast.success("Copied to clipboard!");
      } catch (error) {
        // silent
      }
    }
  };
  
  const nextSlide = () => {
    if (data?.insights) {
      setCurrentIndex((prev) => (prev + 1) % data.insights.length);
    }
  };
  
  const prevSlide = () => {
    if (data?.insights) {
      setCurrentIndex((prev) => (prev - 1 + data.insights.length) % data.insights.length);
    }
  };
  
  const getInsightColor = (type) => {
    switch (type) {
      case "top_continent": return "from-coral to-coral/60";
      case "best_year": return "from-emerald-500 to-emerald-500/60";
      case "peak_month": return "from-amber-500 to-amber-500/60";
      case "repeat_visits": return "from-purple-500 to-purple-500/60";
      case "quarterly_average": return "from-blue-500 to-blue-500/60";
      case "next_continent": return "from-pink-500 to-pink-500/60";
      case "travel_style": return "from-teal-500 to-teal-500/60";
      default: return "from-white/20 to-white/10";
    }
  };
  
  if (loading) {
    return (
      <Card className="bg-charcoal/95 border-coral/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  const currentInsight = data?.insights?.[currentIndex];
  
  return (
    <>
      <Card className="bg-charcoal/95 border-coral/20 backdrop-blur-xl overflow-hidden">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-white">
              <Sparkles className="w-5 h-5 text-coral" />
              Travel Insights
            </CardTitle>
            {!hasAccess && (
              <Badge variant="outline" className="border-coral/50 text-coral text-xs">
                <Lock className="w-3 h-3 mr-1" />
                Pro
              </Badge>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="p-4">
          {hasAccess && data?.insights?.length > 0 && (
            <>
              {/* Carousel */}
              <div className="relative">
                {/* Current insight card */}
                <div 
                  className={`rounded-xl p-6 bg-gradient-to-br ${getInsightColor(currentInsight?.type)} min-h-[180px] flex flex-col justify-between`}
                >
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      {(() => {
                        const IconComponent = iconMap[currentInsight?.icon] || Sparkles;
                        return <IconComponent className="w-6 h-6 text-white/80" />;
                      })()}
                      <span className="text-sm text-white/70">{currentInsight?.title}</span>
                    </div>
                    <p className="text-3xl font-bold text-white mb-2">{currentInsight?.value}</p>
                    <p className="text-sm text-white/70">{currentInsight?.detail}</p>
                  </div>
                  
                  {currentInsight?.shareable && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleShare(currentInsight)}
                      className="self-end text-white/70 hover:text-white hover:bg-white/10"
                    >
                      <Share2 className="w-4 h-4 mr-1" />
                      Share
                    </Button>
                  )}
                </div>
                
                {/* Navigation buttons */}
                {data.insights.length > 1 && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={prevSlide}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-charcoal/50 text-white hover:bg-charcoal/80 rounded-full"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={nextSlide}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-charcoal/50 text-white hover:bg-charcoal/80 rounded-full"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </Button>
                  </>
                )}
              </div>
              
              {/* Dots indicator */}
              {data.insights.length > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  {data.insights.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentIndex(idx)}
                      className={`w-2 h-2 rounded-full transition-colors ${
                        idx === currentIndex ? 'bg-coral' : 'bg-white/20'
                      }`}
                    />
                  ))}
                </div>
              )}
              
              <p className="text-center text-xs text-white/40 mt-3">
                Based on {data.total_trips_analyzed} trips analyzed
              </p>
            </>
          )}
          
          {/* Teaser for non-premium */}
          {!hasAccess && (
            <div className="text-center py-6">
              <div className="relative">
                {/* Blurred preview cards */}
                <div className="grid grid-cols-2 gap-2 blur-sm select-none mb-4">
                  {data?.teaser?.preview?.map((preview, idx) => (
                    <div 
                      key={idx}
                      className="bg-gradient-to-br from-coral/30 to-coral/10 rounded-lg p-3"
                    >
                      <p className="text-xs text-white/60">{preview.title}</p>
                      <p className="text-lg font-bold text-white/40">???</p>
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="bg-charcoal/80 rounded-lg p-4">
                <Sparkles className="w-8 h-8 text-coral mx-auto mb-2" />
                <p className="text-white font-medium mb-1">
                  {data?.teaser?.insights_count || 0} insights waiting
                </p>
                <p className="text-xs text-white/60 mb-3">{data?.teaser?.message}</p>
                <Button
                  onClick={() => setShowPaywall(true)}
                  className="bg-coral hover:bg-coral/90"
                >
                  Learn more on duolyst.co.uk
                </Button>
              </div>
            </div>
          )}
          
          {/* Empty state */}
          {hasAccess && (!data?.insights || data.insights.length === 0) && (
            <div className="text-center py-6">
              <Sparkles className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/60 text-sm">{data?.message || "Complete trips to generate insights!"}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {showPaywall && (
        <PaywallScreen
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          type={PaywallType.FEATURE_GATE}
          feature="premium_insights"
          title="Premium Insights"
          description="Discover hidden patterns in your travel history."
        />
      )}
    </>
  );
};

export default PremiumInsights;
